<template>
  <div>
    <v-row>
      <v-col cols="12">
        <v-card class="mb-4">
          <v-card-title>{{ formTitle }}</v-card-title>
        </v-card>
        <v-card class="m-4">
          <v-row class="d-flex">
            <v-col cols="12" md="4" lg="6">
              <v-card-text>
                <div class="d-flex flex-column">
                  <span class="title">{{ report.filer_name }}</span>
                  <span>{{ report.election_year }}</span>
                  <span v-if="report.type === 'Candidate'">{{
                      report.office.toLowerCase().split('').map((c, i) => i === 0 ? c.toUpperCase() : c).join('')
                    }}</span>
                  <span v-if="report.type === 'Candidate'">{{ report.jurisdiction }}</span>
                </div>
              </v-card-text>
            </v-col>
            <v-col cols="12" md="8" lg="6">
              <v-card class="mx-6" elevation="0">
                <v-card-title>{{
                    report.origin === 'C3' ? `Deposit date: ${formatDate(report.report_from) || 'Unknown'}` : `Period: ${formatDate(this.report.report_from)} - ${formatDate(this.report.report_through)}`
                  }}
                </v-card-title>
                <v-card-text>
                  <div class="report--data">
                    <span><strong>Received:</strong> {{ formatDate(report.receipt_date) }}</span>
                    <span v-if="report.amends_report"><strong>Amends:</strong> {{ report.amends_report }} </span>
                    <span v-if="report.amended_by_report"><strong>Amended by:</strong> {{
                        report.amended_by_report
                      }}</span>
                    <span v-if="report.user_data"><strong>Treasurer name:</strong> {{
                        report.user_data.treasurer ? report.user_data.treasurer.name : ''
                      }}</span>
                    <span v-if="report.user_data">{{
                        report.user_data.final_report ? 'This is the final report.' : ''
                      }}</span>
                  </div>
                  <slot></slot>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
    <v-row cols="12" v-if="report.origin === 'C3' && report.user_data">
      <v-col>
        <v-card>
          <v-card-title>
            Report Summary
          </v-card-title>
          <v-card-text>
            <div><strong>Anonymous contributions:</strong>
              {{ formatMoney(report.metadata.sums.anonymous.amount) || 'None reported' }}
            </div>
            <div><strong>Personal funds deposited:</strong>
              {{ formatMoney(report.metadata.sums.personal.amount) || 'None reported' }}
            </div>
            <div><strong>Loans:</strong>
              {{ formatMoney(report.metadata.sums.loans.amount) || 'None reported' }}
            </div>
            <div><strong>Miscellaneous receipts:</strong>
              {{ formatMoney(report.metadata.sums.misc_auction.amount) || 'None reported' }}
            </div>
            <div><strong>Small contributions:</strong>
              {{ formatMoney(report.metadata.sums.small.amount) || 'None reported' }}
            </div>
            <div><strong>Funds deposited:</strong>
              {{ formatMoney(report.metadata.sums.itemized_contributions.amount) || 'None reported' }}
            </div>
            <div><strong>Deposit date:</strong>
              {{ formatDate(report.report_from) || 'None Available' }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="report.origin === 'C4' && report.user_data">
      <v-col>
        <v-card class="ma-0">
          <v-card-title>
            {{ report.report_type }} Report summary
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <div class="title mb=4">Receipts</div>
                <div><strong>Previous total cash/in-kind:</strong>
                  {{ formatMoney(report.metadata.sums.receipts.previous_receipts.amount) || 'None reported' }}
                </div>
                <div><strong>Receipts received during reporting period:</strong>
                  {{ formatMoney(report.metadata.sums.receipts.cash_receipts.amount) || 'None reported' }}
                </div>
                <div><strong>In-kind contributions received: </strong>
                  {{ formatMoney(report.metadata.sums.receipts.inkind_receipts.amount) || 'None reported' }}
                </div>
                <div><strong>Loan principal repayments:</strong>
                  {{ formatMoney(report.metadata.sums.receipts.loan_principle_paid.amount) || 'None reported' }}
                </div>
                <div><strong>Corrections:</strong>
                  {{ formatMoney(report.metadata.sums.receipts.receipt_corrections.amount) || 'None reported' }}
                </div>
                <div><strong>Total pledge due:</strong>
                  {{ formatMoney(report.metadata.sums.receipts.pledges.amount) || 'None reported' }}
                </div>
                <v-divider></v-divider>
                <div><strong>Total cash/in-kind contributions during campaign:</strong>
                  {{ formatMoney(report.metadata.sums.receipts.campaign_receipts.amount) || 'None reported' }}
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="title mb=4">Expenditures</div>
                <div><strong>Previous total cash/in-kind:</strong>
                  {{ formatMoney(report.metadata.sums.expenditures.previous_expenditures.amount) || 'None reported' }}
                </div>
                <div><strong>Total cash expenditures made during reporting period:</strong>
                  {{ formatMoney(report.metadata.sums.expenditures.cash_expenditures.amount) || 'None reported' }}
                </div>
                <div><strong>In-kind expenditures made:</strong>
                  {{ formatMoney(report.metadata.sums.expenditures.inkind_expenditures.amount) || 'None reported' }}
                </div>
                <div><strong>Loan principal repayments:</strong>
                  {{ formatMoney(report.metadata.sums.expenditures.loan_principle_paid.amount) || 'None reported' }}
                </div>
                <div class="mb-5"><strong>Corrections:</strong>
                  {{ formatMoney(report.metadata.sums.expenditures.expense_corrections.amount) || 'None reported' }}
                </div>
                <v-divider></v-divider>
                <div><strong>Total cash/in-kind expenditures during campaign:</strong>
                  {{ formatMoney(report.metadata.sums.expenditures.campaign_expenses.amount) || 'None reported' }}
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-card class="mt-2">
      <v-card-title>List of attachments</v-card-title>
      <v-card-text>
        <v-data-table
            :headers="headers"
            :items="report.attachments"
        >
          <template v-slot:item.file_name="{item}">
            <a target="_blank" :href="item.file_service_url">
              {{ item.file_name }}
            </a>
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions>
        <slot name="reportAttachment"></slot>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import globalFilters from '@/filters'

export default {
  name: "ReportSummary",
  props: {
    report: Object
  },
  data() {
    return {
      headers: [
        {text: "File name", value: "file_name"},
        {text: "Description", value: "description"}
      ]
    }
  },
  computed: {
    formTitle() {
      return (this.report.origin === 'C3' ? 'Cash Receipts Monetary Contributions - C3' : 'Campaign Summary Receipts & Expenditures - C4')
    },
  },
  methods: {
    ...globalFilters.filters,
  }
}
</script>

<style scoped>
.report--data {
  margin-top: -15px;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
}
</style>
